import { Link } from "react-router-dom";
import { Heart, Mail, Phone, MapPin, ExternalLink, Instagram, Youtube, Facebook, MessageCircle } from "lucide-react";
import LotusIcon from "@/components/icons/LotusIcon";
import siwonLogo from "@/assets/siwon-logo.png";

// Social media links
const socialLinks = {
  instagram: "https://www.instagram.com/vinod_kumar_siwongalaxy?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==",
  youtube: "http://www.youtube.com/@SIWONGALAXY",
  facebook: "https://www.facebook.com/groups/1106650880640583/?ref=share&mibextid=NSMWBT",
  whatsapp: "https://chat.whatsapp.com/JKBlidylfvz4OVhecZ0RYM?mode=gi_t",
  email: "siwongalaxy108@gmail.com"
};

// Social media icon component with brand colors
const SocialIcon = ({ type, size = "normal" }) => {
  const sizeClasses = size === "small" ? "w-8 h-8" : "w-10 h-10";
  const iconSize = size === "small" ? "w-4 h-4" : "w-5 h-5";
  const baseClass = `${sizeClasses} rounded-full flex items-center justify-center transition-transform hover:scale-110`;
  
  const configs = {
    instagram: {
      bg: "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400",
      icon: <Instagram className={`${iconSize} text-white`} />,
      href: socialLinks.instagram,
      label: "Instagram"
    },
    youtube: {
      bg: "bg-red-600",
      icon: <Youtube className={`${iconSize} text-white`} />,
      href: socialLinks.youtube,
      label: "YouTube"
    },
    facebook: {
      bg: "bg-blue-600",
      icon: <Facebook className={`${iconSize} text-white`} />,
      href: socialLinks.facebook,
      label: "Facebook"
    },
    whatsapp: {
      bg: "bg-green-500",
      icon: <MessageCircle className={`${iconSize} text-white`} />,
      href: socialLinks.whatsapp,
      label: "WhatsApp"
    },
    email: {
      bg: "bg-red-500",
      icon: <Mail className={`${iconSize} text-white`} />,
      href: `mailto:${socialLinks.email}`,
      label: "Email"
    }
  };

  const config = configs[type];
  if (!config) return null;

  return (
    <a
      href={config.href}
      target={type === 'email' ? '_self' : '_blank'}
      rel="noopener noreferrer"
      className={`${baseClass} ${config.bg}`}
      aria-label={config.label}
      data-testid={`social-${type}`}
    >
      {config.icon}
    </a>
  );
};

const Footer = ({ onStartQuiz }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground pt-16 pb-12">
      <div className="container">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img 
                src={siwonLogo} 
                alt="Siwon Galaxy" 
                className="h-16 w-auto bg-white/10 rounded-lg p-2"
              />
            </Link>
            <p className="text-primary-foreground/80 text-sm leading-relaxed mb-4">
              Transforming relationships and lives through ancient Vedantic wisdom 
              and modern coaching methodologies.
            </p>
            <p className="font-serif text-lg italic text-accent mb-4">
              "From Silence to Wonderment"
            </p>
            
            {/* Social Media Icons */}
            <div className="flex items-center gap-3" data-testid="footer-social-icons">
              <SocialIcon type="instagram" />
              <SocialIcon type="youtube" />
              <SocialIcon type="facebook" />
              <SocialIcon type="whatsapp" />
              <SocialIcon type="email" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4 flex items-center gap-2">
              <LotusIcon size={20} className="text-accent" />
              Quick Links
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-primary-foreground/80 hover:text-accent transition-colors text-sm">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-primary-foreground/80 hover:text-accent transition-colors text-sm">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/membership-programs" className="text-primary-foreground/80 hover:text-accent transition-colors text-sm">
                  Membership & Programs
                </Link>
              </li>
              <li>
                <Link to="/harmony-app" className="text-primary-foreground/80 hover:text-accent transition-colors text-sm">
                  Harmony App
                </Link>
              </li>
              <li>
                <Link to="/wisdom-board" className="text-primary-foreground/80 hover:text-accent transition-colors text-sm">
                  Wisdom Board
                </Link>
              </li>
              <li>
                <Link to="/the-path" className="text-primary-foreground/80 hover:text-accent transition-colors text-sm">
                  Spiritual Path
                </Link>
              </li>
            </ul>
          </div>

          {/* Discovery Tools */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4 flex items-center gap-2">
              <Heart size={20} className="text-accent" />
              Discovery Tools
            </h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/discovery/relationship-quiz" 
                  className="text-primary-foreground/80 hover:text-accent transition-colors text-sm"
                >
                  Relationship Harmony Quiz
                </Link>
              </li>
              <li>
                <Link 
                  to="/discovery/life-quiz" 
                  className="text-primary-foreground/80 hover:text-accent transition-colors text-sm"
                >
                  Life Transformation Quiz
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-lg font-semibold mb-4 flex items-center gap-2">
              <Mail size={20} className="text-accent" />
              Contact Us
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm">
                <Mail size={16} className="text-accent mt-0.5 flex-shrink-0" />
                <a href="mailto:siwongalaxy108@gmail.com" className="text-primary-foreground/80 hover:text-accent transition-colors">
                  siwongalaxy108@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Phone size={16} className="text-accent mt-0.5 flex-shrink-0" />
                <a href="tel:+917042106100" className="text-primary-foreground/80 hover:text-accent transition-colors">
                  +91 7042106100
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <MapPin size={16} className="text-accent mt-0.5 flex-shrink-0" />
                <span className="text-primary-foreground/80">
                  Siwon Health Care Resources Pvt. Ltd.<br />
                  Gurgaon, Haryana, India
                </span>
              </li>
              <li className="text-xs text-primary-foreground/60 pl-6">
                CIN: U85110HR2003PTC069710<br />
                GSTIN: 06AAHCS1570M1Z3
              </li>
              <li className="flex items-start gap-2 text-sm">
                <ExternalLink size={16} className="text-accent mt-0.5 flex-shrink-0" />
                <a 
                  href="https://learn.siwongalaxy.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary-foreground/80 hover:text-accent transition-colors"
                >
                  Learning Portal
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-primary-foreground/20">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs md:text-sm">
              <Link to="/terms" className="text-primary-foreground/60 hover:text-accent transition-colors">
                Terms
              </Link>
              <Link to="/privacy-policy" className="text-primary-foreground/60 hover:text-accent transition-colors">
                Privacy
              </Link>
              <Link to="/refund-policy" className="text-primary-foreground/60 hover:text-accent transition-colors">
                Refund Policy
              </Link>
              <Link to="/shipping-policy" className="text-primary-foreground/60 hover:text-accent transition-colors">
                Shipping
              </Link>
              <Link to="/contact" className="text-primary-foreground/60 hover:text-accent transition-colors">
                Contact Us
              </Link>
            </div>
            
            <p className="text-primary-foreground/60 text-sm">
              © {currentYear} Siwon Health Care Resources Pvt. Ltd. (Siwon Galaxy). All rights reserved.
            </p>
            
            {/* Social Icons - Bottom (smaller, for mobile visibility) */}
            <div className="flex items-center gap-2 md:hidden" data-testid="footer-social-icons-mobile">
              <SocialIcon type="instagram" size="small" />
              <SocialIcon type="youtube" size="small" />
              <SocialIcon type="facebook" size="small" />
              <SocialIcon type="whatsapp" size="small" />
              <SocialIcon type="email" size="small" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
